import { useEffect, useState } from 'react'
import { ErrorConstant, ReportApprovalService } from 'utility'
import {
    normalizeApprovalDetail,
    normalizeApprovalHistory,
    normalizeWorkflowConfig,
} from 'page-components/report-approval/report-approval-utils'

// OverallStatus 0 + a CurrentStep = an approval step is waiting (GET Approval/Report/{id}/History)
const pendingStepOf = result => {
    const root = result?.Data ?? result?.data ?? result ?? {}
    const overall = root.OverallStatus ?? root.overallStatus
    const step = root.CurrentStep ?? root.currentStep
    return Number(overall) === 0 && step != null ? Number(step) : null
}

export const GetApprovalReportHistory = completedReportId => {
    const [data, setData] = useState([])
    const [pendingStep, setPendingStep] = useState(null)
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        if (!completedReportId) {
            setData([])
            setPendingStep(null)
            return undefined
        }

        let cancelled = false

        const fetchData = async () => {
            try {
                setLoading(true)
                setError('')
                const result = await ReportApprovalService.getReportHistory(
                    completedReportId,
                )
                if (cancelled) return
                setData(normalizeApprovalHistory(result))
                setPendingStep(pendingStepOf(result))
            } catch {
                if (!cancelled) {
                    setData([])
                    setPendingStep(null)
                    setError('')
                }
            } finally {
                if (!cancelled) setLoading(false)
            }
        }

        fetchData()
        return () => {
            cancelled = true
        }
    }, [completedReportId])

    const refresh = async () => {
        if (!completedReportId) return []
        try {
            const result = await ReportApprovalService.getReportHistory(
                completedReportId,
            )
            const normalized = normalizeApprovalHistory(result)
            setData(normalized)
            setPendingStep(pendingStepOf(result))
            return normalized
        } catch {
            setData([])
            setPendingStep(null)
            return []
        }
    }

    return { data, error, loading, refresh, setData, pendingStep }
}

export const GetApprovalWorkflowConfig = templateId => {
    const [data, setData] = useState(null)
    const [raw, setRaw] = useState(null)
    const [error, setError] = useState('')
    // loading from the first render: the screen must not show an editable form before the real workflow is read
    const [loading, setLoading] = useState(Boolean(templateId))

    useEffect(() => {
        // a failed / missing load must not look like "no workflow yet": an empty form saved over the real
        // workflow would replace it (2026-10-22). The server answers an empty config (ConfigId 0) when none exists.
        setData(null)
        setRaw(null)
        if (!templateId) {
            setLoading(false)
            return undefined
        }

        let cancelled = false

        const fetchData = async () => {
            try {
                setLoading(true)
                setError('')
                const result = await ReportApprovalService.getWorkflowConfig(
                    templateId,
                )
                if (cancelled) return
                const configId = Number(result?.ConfigId ?? result?.configId)
                if (!result || typeof result !== 'object' || !Number.isFinite(configId)) {
                    throw new Error('not a workflow config')
                }
                setRaw(result)
                setData(normalizeWorkflowConfig(result))
            } catch (err) {
                if (!cancelled) {
                    setRaw(null)
                    setData(null)
                    setError(
                        err?.status === 403
                            ? 'Only a company admin can change the approval workflow.'
                            : 'The approval workflow could not be loaded. Nothing has been changed - please reload the page.',
                    )
                }
            } finally {
                if (!cancelled) setLoading(false)
            }
        }

        fetchData()
        return () => {
            cancelled = true
        }
    }, [templateId])

    return { data, raw, error, loading, setData }
}

// Approval history for the no-login approval page (by link token).
export const GetApprovalSignHistory = token => {
    const [data, setData] = useState([])
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        if (!token) {
            setData([])
            return undefined
        }
        let cancelled = false
        setLoading(true)
        ReportApprovalService.getSignHistory(token)
            .then(result => {
                if (!cancelled) setData(normalizeApprovalHistory(result))
            })
            .catch(() => {
                if (!cancelled) setData([])
            })
            .finally(() => {
                if (!cancelled) setLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [token])

    return { data, loading }
}

export const GetApprovalSignDetail = token => {
    const [data, setData] = useState(null)
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        if (!token) {
            setData(null)
            return undefined
        }

        let cancelled = false

        const fetchData = async () => {
            try {
                setLoading(true)
                setError('')
                const result = await ReportApprovalService.getSignDetail(token)
                if (cancelled) return
                setData(normalizeApprovalDetail(result))
            } catch (err) {
                // 410 LINK_INVALID: expired, already used, or the step is no longer waiting
                if (!cancelled) {
                    setError(
                        err?.status === 410
                            ? err?.error?.ErrorMessage ||
                                  'This approval link has expired or was already used.'
                            : ErrorConstant.default,
                    )
                }
            } finally {
                if (!cancelled) setLoading(false)
            }
        }

        fetchData()
        return () => {
            cancelled = true
        }
    }, [token])

    return { data, error, loading, setData }
}
